/* :Page:MotorRenewal: Renewable policies list page */

import { unwrapResult } from "@reduxjs/toolkit";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import Loader from "../../../../components/common/Loader/Loader";
import TitleDescription from "../../../../components/newMotorPolicy/titleDescription/titleDescription";
import { PROMO_CODE } from "../../../../components/newMotorPolicy/veiwSummaryPopup/viewSummaryPopup";
import PolicyCard from "../../../../components/policyCard/policyCard";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import useMotorForm from "../../../../hooks/useMotorFormData";
import useRenewalFlow, {
  RENEW_POLICY_EXPIRED,
} from "../../../../hooks/useRenewalFlow";
import { selectLoginTokenInfo } from "../../../../redux/features/loginRegisterSlice";
import {
  getCoverPrices,
  getCoverPricesLoading,
} from "../../../../redux/features/motor/getQuoteSlice";
import { motorRenewalPremium } from "../../../../redux/features/motor/renewalPremiumSlice";
import {
  fetchUserPolicies,
  selectUserPoliciesData,
  selectUserPoliciesLoader,
} from "../../../../redux/features/myPolicySlice";
import { clearApplyPromoState } from "../../../../redux/features/promoCode/promoCodeSlice";
import { getClientSource } from "../../../../utils/getClientSource";
import { screenResolution } from "../../../../utils/screenResolution";
import { coverPriceRequest } from "../../newMotorPolicy/planPages/data";
import RenewMotorLayout from "../renewMotorLayout";

export const RENEW_POLICY_SELECTED_POLICY = "RENEW_POLICY_SELECTED_POLICY";
const SelectPoliciesPage = () => {
  /* Setting up dispatch to call redux */
  const dispatch = useDispatch();
  const translate = useLanguageTranslate();
  const [formState, setFormState] = useState(null);

  /* Setting up variables with appropriate requests to send and response to receive, reading from redux/functions */
  const [selected, setSelected] = useState();
  const loginTokenInfo = useSelector(selectLoginTokenInfo);
  let isLoggedin = loginTokenInfo;
  const fetchPoliciesLoading = useSelector(selectUserPoliciesLoader);
  const coverPricesLoading = useSelector(getCoverPricesLoading);
  /* Functions and Variables that are updated with functions/state */

  const { t } = useTranslation();
  const { handleNextSubstep, handleFlowChange } = useRenewalFlow();
  const { saveMotorDataInRedux } = useMotorForm(
    RENEW_POLICY_SELECTED_POLICY,
    setSelected
  );
  const { removeMotorFormPromoCodeData } = useMotorForm(
    PROMO_CODE,
    setFormState
  );

  // later can take polices from below selector
  var userPolicies = useSelector(selectUserPoliciesData);
  // const userPolicies = USER_POLICIES;

  // Helper function to extract base policy number from full policy number
  const getBasePolicyNumber = (fullPolicyNumber) => {
    const dashIndex = fullPolicyNumber.lastIndexOf("-");
    if (dashIndex !== -1) {
      // Get everything before the dash, then extract the last 7 digits
      const beforeDash = fullPolicyNumber.substring(0, dashIndex);
      const lastSlashIndex = beforeDash.lastIndexOf("/");
      return lastSlashIndex !== -1
        ? beforeDash.substring(lastSlashIndex + 1)
        : beforeDash;
    }
    // Fallback: if no dash found, use the full policy number
    return fullPolicyNumber;
  };

  // Filter active renewable policies.
  // Hide policies already renewed (Renewed === "Yes") — successor exists.
  const activeRenewablePolicies = userPolicies?.filter(
    (policy) =>
      policy.IsRenewable &&
      policy.PolicyStatus == "ACTIVE POLICY" &&
      policy.Renewed !== "Yes"
  );

  // Create a set of base policy numbers for active policies
  const activePolicyBaseNumbers = new Set(
    activeRenewablePolicies?.map((policy) =>
      getBasePolicyNumber(policy.PolicyNumber)
    ) || []
  );

  // Filter expired policies (only motor policies with product code 0101), exclude those with active versions,
  // policies already renewed, and policies the API marks as not renewable.
  const expiredPolicies = userPolicies?.filter((policy) => {
    if (
      policy.PolicyStatus !== "EXPIRED" ||
      policy.ProductCode !== "0101" ||
      policy.Renewed === "Yes" ||
      !policy.IsRenewable
    ) {
      return false;
    }

    // Exclude expired policies that have active counterparts
    const basePolicyNumber = getBasePolicyNumber(policy.PolicyNumber);
    return !activePolicyBaseNumbers.has(basePolicyNumber);
  });

  // Group expired policies by base policy number and get the latest one for each
  const latestExpiredPoliciesMap = expiredPolicies?.reduce((acc, current) => {
    const basePolicyNumber = getBasePolicyNumber(current.PolicyNumber);

    if (!acc[basePolicyNumber]) {
      acc[basePolicyNumber] = current;
    } else {
      // Compare dates to keep the latest one, or compare policy numbers if dates are the same
      const existingDate = new Date(
        acc[basePolicyNumber].EndDate || acc[basePolicyNumber].CreatedDate || 0
      );
      const currentDate = new Date(current.EndDate || current.CreatedDate || 0);

      if (currentDate > existingDate) {
        acc[basePolicyNumber] = current;
      } else if (currentDate.getTime() === existingDate.getTime()) {
        // If dates are the same, compare the suffix after the dash to get the higher version (-04 > -01)
        const existingSuffix =
          acc[basePolicyNumber].PolicyNumber.split("-").pop();
        const currentSuffix = current.PolicyNumber.split("-").pop();
        if (currentSuffix > existingSuffix) {
          acc[basePolicyNumber] = current;
        }
      }
    }

    return acc;
  }, {});

  // Convert the map back to an array of latest expired policies
  const latestExpiredPolicies = latestExpiredPoliciesMap
    ? Object.values(latestExpiredPoliciesMap)
    : [];

  // Combine active renewable policies with the latest expired policies
  const renewablePolicies = [
    ...(activeRenewablePolicies || []),
    ...latestExpiredPolicies,
  ];

  /* Use Effects, monitor changes to call on [var] change or one time call */
  if (isLoggedin) {
    console.log("LOGGED IN!!");
    // Pages index for RENEW_POLICY_LOGGED_IN: 1 Policies, 2 Sum Insured, 3 Covers
  } else {
    // Pages index for default RENEW_POLICY_SAME_PLAN: 0 CPR, 1 OTP, 2 Policies, 3 Review Plan (Flow can change, below added), 4 Sum Insured, 5 Covers, 6 Payment Summary
    // Pages index for default RENEW_POLICY_SAME_PLAN with addition of Change Plan RENEW_POLICY_DOWNGRADE_PLAN: 4 Change plan 5 Covers ...
    console.log("NOT LOGGED IN!!");
    // handleFlowChange(RENEW_POLICY_SAME_PLAN, );
  }

  useEffect(() => {
    // Always refetch on mount so the list reflects the latest server state.
    // Cached policies stay visible during the refresh (same-user pending
    // handler in the slice) and are replaced when the fresh response arrives.
    if (!isLoggedin?.userName) return;
    const payload = {
      Cpr: isLoggedin?.userName,
      Channel: getClientSource(),
    };
    dispatch(fetchUserPolicies(payload));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-process pre-selected policy handed off from /renew. The /renew page
  // stashes the raw policy in redux (no premium fields yet) and routes here at
  // step 0. We detect that raw shape and run the normal continue flow once so
  // the user is taken straight to REVIEW_YOUR_POLICY without an extra click.
  const autoProcessedRef = useRef(false);
  useEffect(() => {
    if (autoProcessedRef.current) return;
    if (!selected) return;
    // Raw policy = no premium-derived fields
    const isRaw = !selected.OriginalPlanName && !selected.QuoteDetails;
    if (!isRaw) return;
    autoProcessedRef.current = true;
    continueClickHanlder(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const selectClickHandler = async (data) => {
    setSelected(data);
  };

  /* Next */
  const continueClickHanlder = async (policyOverride) => {
    const target = policyOverride || selected;
    if (!target) {
      toast.error("Select at least one policy");
      return;
    }
    console.log("Selected Plan is: ", target);

    // Check if policy is expired - skip premium API call and go directly to upload photos
    if (target.PolicyStatus === "EXPIRED") {
      try {
        // Save selected policy data for later use after photo upload
        saveMotorDataInRedux(target);
        // Clear promo code
        await dispatch(clearApplyPromoState());
        await removeMotorFormPromoCodeData();
        // Change to expired policy flow - start from upload photos step
        handleFlowChange(RENEW_POLICY_EXPIRED, 1);
      } catch (error) {
        toast.error("Failed to proceed with expired policy renewal");
      }
      return;
    }

    // For active policies, continue with normal premium API flow
    try {
      // Add default covers for platinum
      const motorRenewalPremiumPayload = {
        PolicyNumber: target.PolicyNumber,
        Channel: getClientSource(),
        Covers: [],
        DefaultCovers: false,
        NewPlan: target.PlanName,
      };

      // Clear promo code
      await dispatch(clearApplyPromoState());
      await removeMotorFormPromoCodeData();
      // Calling API /policy/premium
      const motorRenewalPremiumAction = await dispatch(
        motorRenewalPremium(motorRenewalPremiumPayload)
      );
      const motorRenewalPremiumResult = unwrapResult(motorRenewalPremiumAction);

      console.log("Premium result is: ", motorRenewalPremiumResult);
      console.log("Premium new quote is: ", motorRenewalPremiumResult.PolicyNumber);
      console.log("Premium total is: ", motorRenewalPremiumResult.TotalPremium);

      if (motorRenewalPremiumResult) {
        // Call covers prices
        // Cover prices
        const coverPricesPayload = coverPriceRequest(
          target.PolicyNumber,
          target.PolicyNumber.PlanName,
          "",
          ""
        );
        await dispatch(getCoverPrices(coverPricesPayload));

        // Add the string originalPlanName to the result
        const updatedPolicyPremiumResult = {
          ...motorRenewalPremiumResult, // Spread the original policy premium result
          OriginalPlanName: motorRenewalPremiumResult.PlanName, // Add the OriginalPlanName field
          OriginalPolicyNo:
            motorRenewalPremiumResult.QuoteDetails.PreviousPolicyNumber, // Add the OriginalPolicyNo field
        };

        saveMotorDataInRedux(updatedPolicyPremiumResult);
        handleNextSubstep();
      }
    } catch (error) {
      // alert("Could not get policy premium.")
      try {
        toast.error(translate(error.ErrorMessageEn, error.ErrorMessageAr));
      } catch {
        toast.error("Could not get policy premium.");
      }
    }
  };

  return (
    <RenewMotorLayout
      is_hide_back_btn
      show_close_btn
      btn_onClick={continueClickHanlder}
      btn_disabled={!renewablePolicies || renewablePolicies.length == 0}
    >
      <div className="selectpolicy p-4 w-[33%] max-350:w-[100%] max767:w-[100%] ipad:w-[55%] mx-auto max767:p-5 max767:pt-0">
        <TitleDescription
          title={t("motorInsurance.renewalPolicyFlow.selectPoliciesPage.title")}
          description={
            screenResolution.mobile
              ? "Select Your Policy to renew"
              : t(
                  "motorInsurance.renewalPolicyFlow.selectPoliciesPage.description"
                )
          }
          className="mb-6"
        />
        <div className=" ">
          <div className="bg-gray-100 p-4 rounded-[26px] max767:bg-white max767:p-0">
            {!screenResolution.mobile && (
              <p className="mb-4 text-center text-[#48535B] text-base not-italic font-medium leading-[26px] tracking-[-0.16px] max767:text-left">
                {translate(
                  "Select Your Policy to renew",
                  "اختر وثيقتك للتجديد"
                )}
              </p>
            )}
            <div className="grid justify-center gap-4 policycardbox">
              {(() => {
                if (!renewablePolicies || renewablePolicies.length == 0) {
                  if (fetchPoliciesLoading || coverPricesLoading) {
                    return <Loader />;
                  } else {
                    return (
                      <p className="text-center">
                        {translate(
                          "You have no policies due for renewal",
                          "ليس لديك وثائق مستحقة للتجديد"
                        )}
                      </p>
                    );
                  }
                }

                return [
                  [
                    [
                      [
                        renewablePolicies.map((each, index) => (
                          <PolicyCard
                            key={index}
                            policy_name={each.policy_name}
                            policy_label={each.policy_label}
                            policy_number={each.policy_number}
                            from_date={each.from_date}
                            to_date={each.to_date}
                            policy_bg_image={each.policy_bg_image}
                            expire_days={each.expire_days}
                            is_text_dark={each.is_text_dark}
                            mapping_key={each.id}
                            data={each}
                            showSelectButton
                            isSelected={
                              selected?.PolicyNumber == each?.PolicyNumber
                            }
                            selectClickHandler={() => selectClickHandler(each)}
                          />
                        )),
                      ],
                    ],
                  ],
                ];
              })()}
            </div>
          </div>
        </div>
      </div>
    </RenewMotorLayout>
  );
};

export default SelectPoliciesPage;
